import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import HLogoutModal from "./HLogoutModal";

import * as localService from "../services/localService";
import { Constants, RoleConstants } from "../utils/constants";
import { getNavigationItems } from "../utils/navigationUtils";
import toastUtils from "../utils/toast";
import { getVersionString } from "../utils/version";
import { FeatureAccess } from "../utils/featureAccessControl";
import logger from "../utils/logger";


const _navIconSize = 16;

export default function HSidebarPro({ onExpandChange }) {
  const location = useLocation();
  const history = useHistory();

  // Read user role from localStorage since Redux auth.user is not populated
  const userRoleCode = useMemo(() => {
    const userData = localService.getUser();
    return userData?.organizationRole?.code;
  }, []);

  const [collapsed, setCollapsed] = useState(true);
  const [locked, setLocked] = useState(() => {
    const saved = localStorage.getItem("sidebarLocked");
    return saved ? JSON.parse(saved) : false;
  });

  const [userInitials, setUserInitials] = useState("");
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userImageUrl, setUserImageUrl] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isAdminRole = useCallback(() => {
    logger.log("🔍 [HSidebarPro] Role Check:", {
      userRoleCode,
      allowedRoles: RoleConstants.ROLE_GROUPS.UPGRADE_ALLOWED_ROLES,
      isIncluded: RoleConstants.ROLE_GROUPS.UPGRADE_ALLOWED_ROLES.includes(userRoleCode),
    });
    return RoleConstants.ROLE_GROUPS.UPGRADE_ALLOWED_ROLES.includes(userRoleCode);
  }, [userRoleCode]);

  const isIndividualCustomer = userRoleCode === "ROLE_HOST_CUSTOMER_INDIVIDUAL";

  const handleSubscriptionClick = useCallback(() => {
    const isAdmin = isAdminRole();
    logger.log("🔍 [HSidebarPro] Subscription Click:", { isAdmin, userRoleCode });
    if (!isAdmin) {
      toastUtils.warning("Contact Admin", {
        description: "Contact your admin to upgrade your account to access more features.",
      });
      return;
    }
    history.push("/host/pricing");
  }, [isAdminRole, history, userRoleCode]);

  useEffect(() => {
    if (locked) setCollapsed(false);
  }, [locked]);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const userData = localService.getUser();
        logger.log("🔍 [HSidebarPro] Loading user data:", { userData, imageUrl: userData?.imageUrl });
        if (userData) {
          const { firstName, lastName, role, organizationRole, imageUrl } = userData;
          const initials = getInitials(firstName, lastName);
          setUserInitials(initials);
          setUserName(`${firstName || ""} ${lastName || ""}`.trim());
          setUserImageUrl(imageUrl || "");
          logger.log("🔍 [HSidebarPro] Set userImageUrl to:", imageUrl);
          // Use organizationRole.name directly or fallback to direct role property
          const userRole = organizationRole?.name || role || "User";
          setUserRole(userRole || "Free Account");
        }
      } catch (e) {
        logger.error("Error loading user data in sidebar:", e);
      }
    };

    // Load user data on mount
    loadUserData();

    // Listen for storage changes to update sidebar when profile is updated
    const handleStorageChange = e => {
      if (e.key === "currentUser" || e.key === "user") {
        loadUserData();
      }
    };

    // Listen for custom events (for same-tab updates)
    const handleProfileUpdate = () => {
      loadUserData();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("profileUpdated", handleProfileUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("profileUpdated", handleProfileUpdate);
    };
  }, []);

  const handleMouseEnter = () => {
    if (locked) return;
    setCollapsed(false);
  };

  const handleMouseLeave = () => {
    if (locked) return;
    setCollapsed(true);
  };

  const toggleLock = () => {
    const next = !locked;
    setLocked(next);
    localStorage.setItem("sidebarLocked", JSON.stringify(next));
    if (next) setCollapsed(false);
  };

  const { mainNavItems, toolsItems } = useMemo(() => getNavigationItems(), []);

  useEffect(() => {
    if (typeof onExpandChange === "function") {
      onExpandChange(!collapsed);
    }
  }, [collapsed, onExpandChange]);

  const isActive = path => location.pathname === path;

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);

      // Clear all user data from localStorage
      localService.clearAllUserData();

      // Close the modal
      setShowLogoutModal(false);

      // Redirect to signin page
      history.push("/signin");
    } catch (error) {
      logger.error("Error during logout:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div
      className="sidebar-content hidden lg:flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        height: "100vh",
        zIndex: 10,
        backgroundColor: "#ffffff",
        borderRight: "1px solid #e5e7eb",
        width: collapsed ? "64px" : "240px",
        transition: "width 0.3s ease-in-out",
        flexDirection: "column",
      }}
    >
      {/* Logo */}
      <div className="px-3 pt-3 pb-1 w-full">
        <div className="flex justify-center items-center relative">
          <button
            onClick={() => history.push("/host/dashboard")}
            className="logo-button"
            title="Go to Dashboard"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              transition: "all 0.3s ease-in-out",
              position: "relative",
              width: "100%",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              className="font-extrabold tracking-[0.25em] text-slate-900 transition-all duration-300"
              style={{
                fontSize: collapsed ? "1.25rem" : "1.5rem",
                letterSpacing: collapsed ? "0" : "0.25em",
              }}
            >
              {collapsed ? "S" : "SPACIA"}
            </span>
          </button>

          {/* Lock Button beside logo - only show when expanded */}
          {!collapsed && (
            <button
              onClick={toggleLock}
              aria-pressed={locked}
              aria-label={locked ? "Unlock sidebar to enable hover" : "Lock sidebar to keep open"}
              className="absolute right-0 p-1 rounded-sm transition-all duration-200"
              style={{
                background: locked ? "rgba(107, 114, 128, 0.15)" : "transparent",
                color: locked ? "#6b7280" : "#cbd5e1",
              }}
              onMouseEnter={e => {
                if (!locked) {
                  e.target.style.background = "rgba(203, 213, 225, 0.08)";
                  e.target.style.color = "#94a3b8";
                }
              }}
              onMouseLeave={e => {
                if (!locked) {
                  e.target.style.background = "transparent";
                  e.target.style.color = "#cbd5e1";
                }
              }}
            >
              <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className="sidebar-nav"
        style={{
          overflowY: "auto",
          paddingInline: 6,
          flex: 1,
          minHeight: 0,
          paddingBottom: 6,
        }}
      >
        <div className="py-1" style={{ display: "grid", gap: 4 }}>
          {mainNavItems.map(item => (
            <NavItem
              key={item.path}
              icon={item.icon}
              label={item.name}
              path={item.path}
              isActive={isActive(item.path)}
              expanded={!collapsed}
            />
          ))}
        </div>

        {/* Tools Section */}
        <div className="mt-3">
          <div
            className="px-2 mb-0"
            style={{
              opacity: collapsed ? 0 : 1,
              transition: "opacity 0.3s ease-in-out",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <h3 className="text-xs font-medium tracking-wider text-gray-500 uppercase whitespace-nowrap">TOOLS</h3>
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            {toolsItems.map(item => (
              <NavItem
                key={item.path}
                icon={item.icon}
                label={item.name}
                path={item.path}
                isActive={isActive(item.path)}
                expanded={!collapsed}
              />
            ))}
          </div>
        </div>
      </nav>

      {/* Bottom Section (sticky area) */}
      <div style={{ padding: "6px", paddingTop: 0 }}>
        {/* Upgrade Plan Section */}
        {FeatureAccess.shouldShowSubscriptionAndPurchasesButton(localService.getUser()) && (
          <div className="upgrade-button-container" style={{ paddingTop: 6 }}>
            <div
              className="p-2 text-center bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg"
              style={{
                border: "1px solid #e9d5ff",
                opacity: collapsed ? 0 : 1,
                transform: collapsed ? "scale(0.95)" : "scale(1)",
                transition: "opacity 0.3s, transform 0.3s",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              <div className="flex justify-center mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={Constants.HOST_EXPERIENCE_BASE_COLOR}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                </svg>
              </div>
              <div className="overflow-hidden">
                <button
                  onClick={handleSubscriptionClick}
                  className="upgrade-button-text whitespace-nowrap"
                  style={{
                    backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                    color: "#fff",
                    width: "100%",
                    borderRadius: 8,
                    padding: "8px 0",
                    fontSize: 12,
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Subscriptions & Purchase
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Profile Section */}
        <div
          className="flex gap-2 items-center p-1.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
          style={{ borderColor: "#e5e7eb" }}
          onClick={() => {
            const userData = localService.getUser();
            const roleCode = userData?.organizationRole?.code;
            // Customer roles go to profile, others go to settings
            const isCustomer = RoleConstants.ROLE_GROUPS.HOST_CUSTOMER_ROLES.includes(roleCode);
            history.push(isCustomer ? "/host/customer-profile" : "/host/settings");
          }}
        >
          <div
            className="flex overflow-hidden flex-shrink-0 justify-center items-center w-8 h-8 rounded-xl"
            style={{
              backgroundColor: userInitials ? "#f0f0f0" : "transparent",
              marginLeft: collapsed ? "3px" : "0px",
            }}
          >
            <img
              src={
                userImageUrl ||
                "https://ui-avatars.com/api/?name=" + encodeURIComponent(userName || "User") + "&background=random"
              }
              alt={userName || "User"}
              className="object-cover w-full h-full"
              onError={e => {
                e.target.onerror = null;
                if (userInitials) {
                  const parent = e.target.parentNode;
                  e.target.style.display = "none";
                  const initialsSpan = document.createElement("span");
                  initialsSpan.className = "text-sm font-medium text-gray-700";
                  initialsSpan.textContent = userInitials;
                  parent.appendChild(initialsSpan);
                } else {
                  e.target.src =
                    "https://ui-avatars.com/api/?name=" + encodeURIComponent(userName || "User") + "&background=random";
                }
              }}
            />
          </div>
          <div className="overflow-hidden flex-1 nav-item-text-container">
            <div
              className="text-transition"
              style={{
                opacity: collapsed ? 0 : 1,
                maxWidth: collapsed ? 0 : "100%",
                transition: "opacity .3s, max-width .3s",
                whiteSpace: "nowrap",
              }}
            >
              <p className="text-sm font-medium truncate whitespace-nowrap">{userName || "User"}</p>
              <p className="text-xs text-gray-500 truncate whitespace-nowrap">{userRole}</p>
            </div>
          </div>
          <button
            onClick={e => {
              e.stopPropagation();
              setShowLogoutModal(true);
            }}
            className="flex justify-center items-center"
            style={{
              width: 28,
              height: 28,
              borderRadius: "9999px",
              opacity: collapsed ? 0 : 1,
              transform: collapsed ? "scale(0.95)" : "scale(1)",
              transition: "opacity .3s, transform .3s",
            }}
          >
            <img src="/img/svgs/logout.svg" alt="Logout" className="w-4 h-4" />
          </button>
        </div>

        {/* Version Number */}
        <div className="pt-1 pb-1 text-center">
          <span className="text-xs text-gray-400">{getVersionString()}</span>
        </div>
      </div>

      <style jsx global>{`
        .sidebar-nav::-webkit-scrollbar {
          display: none;
        }
        .sidebar-nav {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .logo-button {
          user-select: none;
        }
        .upgrade-button-container {
          transform-origin: center;
          backface-visibility: hidden;
        }
        .nav-item-text-container {
          position: relative;
          overflow: hidden;
        }
        .lock-button {
          transition: all 0.2s ease-in-out;
          border-radius: 0.5rem;
        }
      `}</style>

      {/* Logout Modal */}
      <HLogoutModal
        show={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        isLoading={isLoggingOut}
      />
    </div>
  );
}

function _formatRole(role) {
  if (!role) return "User";
  return role
    .replace("ROLE_", "")
    .toLowerCase()
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getInitials(firstName, lastName) {
  let initials = "";
  if (firstName) initials += firstName.charAt(0);
  if (lastName) initials += lastName.charAt(0);
  return initials.toUpperCase();
}

function NavItem({ icon: Icon, label, path, isActive, expanded }) {
  const linkBaseClasses =
    "flex w-full items-center rounded-md text-left text-xs font-medium whitespace-nowrap hover:bg-gray-100 hover:text-gray-900 transition-all duration-300 ease-in-out";

  return (
    <Link
      to={path}
      className={linkBaseClasses}
      style={{
        height: 36,
        paddingLeft: expanded ? 6 : 3,
        paddingRight: expanded ? 6 : 3,
        justifyContent: expanded ? "flex-start" : "center",
        backgroundColor: isActive ? Constants.HOST_EXPERIENCE_BASE_COLOR : "transparent",
        color: isActive ? "#ffffff" : "#000000",
      }}
      aria-current={isActive ? "page" : undefined}
      title={label}
    >
      <div
        className="nav-item-icon"
        style={{
          width: expanded ? 40 : 28,
          minWidth: expanded ? 40 : 28,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "flex-start" : "center",
          paddingLeft: expanded ? 6 : 0,
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Icon className="w-4 h-4" color={isActive ? "#ffffff" : "#000000"} />
      </div>
      <div
        className="overflow-hidden flex-1 nav-item-text-container"
        style={{
          opacity: expanded ? 1 : 0,
          maxWidth: expanded ? "100%" : 0,
          transition: "opacity 0.3s ease-in-out, max-width 0.3s ease-in-out",
          pointerEvents: expanded ? "auto" : "none",
        }}
      >
        <span
          className="text-transition truncate"
          style={{
            display: "block",
            paddingLeft: expanded ? 6 : 0,
            transition: "padding-left 0.3s ease-in-out",
          }}
        >
          {label}
        </span>
      </div>
    </Link>
  );
}
